import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription, finalize } from 'rxjs';
import { IsMobileDevice } from '../../../core/helper/mobile';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { ProtectedIdentityAccess } from '../../model/protected-identity';
import { ProtectedIdentityService } from '../../service/data/protected-identity.service';

@Component({
  selector: 'app-protected-identity-bankid-dialog',
  templateUrl: './protected-identity-bankid-dialog.component.html',
  styleUrls: ['./protected-identity-bankid-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ProtectedIdentityBankIdDialogComponent implements OnInit, OnDestroy {
  @ViewChild('bankIdContainer') bankIdContainer?: ElementRef<HTMLElement>;

  readonly isMobile = IsMobileDevice();
  running = false;
  isAutoStart = false;
  isVerifying = false;
  isFullscreen = false;

  private readonly subscriptions = new Subscription();
  private verificationSubscription?: Subscription;

  constructor(
    private readonly actionWindowService: ActionWindowService,
    private readonly protectedIdentityService: ProtectedIdentityService
  ) {}

  ngOnInit(): void {
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    const element = this.bankIdContainer?.nativeElement;
    if (element && document.fullscreenElement === element) {
      void document.exitFullscreen();
    }
  }

  startBankId(isAutoStart: boolean): void {
    if (this.running || this.isVerifying) return;

    this.isAutoStart = isAutoStart;
    this.running = true;
  }

  onResult(orderReference: string): void {
    if (!orderReference || this.isVerifying) return;

    this.isVerifying = true;
    this.verificationSubscription = this.protectedIdentityService
      .verifyBankId(orderReference)
      .pipe(finalize(() => (this.isVerifying = false)))
      .subscribe({
        next: access => this.complete(access),
        error: () => (this.running = false),
      });
    this.subscriptions.add(this.verificationSubscription);
  }

  cancel(): void {
    if (this.running) {
      this.verificationSubscription?.unsubscribe();
      this.verificationSubscription = undefined;
      this.isVerifying = false;
      this.running = false;
      this.isAutoStart = false;

      const element = this.bankIdContainer?.nativeElement;
      if (element && document.fullscreenElement === element) {
        void document.exitFullscreen();
      }
      return;
    }

    this.actionWindowService.close();
  }

  toggleFullscreen(): void {
    const element = this.bankIdContainer?.nativeElement;
    if (!element) return;

    if (document.fullscreenElement === element) {
      void document.exitFullscreen();
    } else if (!document.fullscreenElement) {
      void element.requestFullscreen();
    }
  }

  private complete(access: ProtectedIdentityAccess): void {
    const element = this.bankIdContainer?.nativeElement;
    if (element && document.fullscreenElement === element) {
      void document.exitFullscreen().finally(() => this.actionWindowService.complete(access));
      return;
    }

    this.actionWindowService.complete(access);
  }

  private readonly onFullscreenChange = (): void => {
    this.isFullscreen = document.fullscreenElement === this.bankIdContainer?.nativeElement;
  };
}
